import { CLIENT_ID, CLIENT_ID_HEADER } from "./clientId";
import { API_BASE } from "./config";
import { bounceIfUnauthorized } from "./session";

// These mirror the host's search DTOs (Nihongonator.Host/Dtos.cs), serialized camelCase.

export interface FuriganaSegment {
  text: string;
  ruby: string | null;
}

export interface ParsedToken {
  surface: string;
  reading: string | null;
  root: string | null;
  start: number;
  length: number;
  furigana: FuriganaSegment[];
  hasEntries: boolean;

  /** Whether the FORM strip appears. False for a plain noun, so the dock starts at the results. */
  inflected: boolean;
  dictionaryForm: string | null;
  /** The collapsed components, e.g. ["た"] for 現れた. */
  components: string[];
  /**
   * The same components, each told whether the dictionary knows it (日本-512).
   *
   * `components` is the FORM strip's display text and stays exactly what it was; this is what its
   * chips are built from, because whether a root is a word is the one thing the client cannot
   * derive — there is no dictionary here.
   *
   * **Optional, and a missing value means inert chips rather than none.** It arrived after
   * `components`, so a tab open across the deploy keeps rendering the strip it always did. That is
   * also why it is a second field: turning `components`' members into objects in place would have
   * drawn `[object Object]` in that tab, silently.
   */
  componentRoots?: { text: string; hasEntries: boolean }[];
  /** Plain language, e.g. "plain past · affirmative". */
  formDescription: string | null;
}

export interface SearchParseResponse {
  tokens: ParsedToken[];
}

export interface Sense {
  partsOfSpeech: string[];
  glosses: string[];
  misc: string[];
  info: string[];
}

export interface Definition {
  term: string;
  primaryReading: string | null;
  kanjiForms: string[];
  readings: string[];
  senses: Sense[];
  priorities: string[];
  isCommon: boolean;
  /**
   * The subset of `priorities` the frequency sort actually orders by (日本-499).
   *
   * Cut on the server, because the two sets it comes from are Python — `NOTABLE_FREQUENCIES` (what
   * enrichment writes as a tag) and `SORT_RANK_FLOORS` (what the sort reads off the note). A copy
   * of that membership here would be exactly the drift `engine/grammar_keys.py` and
   * `engine/markup.py` exist to prevent. What this side owns is the LABELS, in `resultBadges.ts`.
   *
   * Deliberately not `priorities` filtered locally: that list also carries the 48 nfXX bands and
   * the two `Loanword` markers, none of which becomes a tag, so none of which can reach the sort.
   *
   * **Optional, and that is not defensiveness — it is the honest type.** `/api/search/lookup`
   * always sends it, but a `Definition` does not only ever come from there: Ask stores a `dict`
   * result's entries verbatim in `ask_message.content.payload` and re-renders them through the
   * real `ResultCard`, so a thread written before this field existed hands one over without it.
   * Typed as required, the `?? []` that handles that is dead code to the compiler and one
   * "simplification" away from a `TypeError` on the transcript's own render.
   */
  sortMarkers?: string[];
  frequency: number | null;
  jlptLevel: number | null;
  matchLength: number;
  /**
   * JMdict `ent_seq` — sent back on add so the server knows which ROW was clicked. Every entry
   * written the same way shares one card (cards are unique by the Japanese field), so this decides
   * which one leads, not which is kept. `(term, reading)` cannot say it: 位 has two entries
   * reading くらい.
   */
  seq: number;
  /** Every English sense is archaic/obsolete/rare. Still listed and addable, not merged by default. */
  isArchaic: boolean;
  /** How many OTHER entries share this row's card — "Will merge with N other 頭 cards". */
  mergesWith: number;
}

export interface SearchLookupResponse {
  entries: Definition[];
}

export interface AddCardRequest {
  term: string;
  reading?: string | null;
  sentence?: string | null;
  /** PRD §6 removes the toggle - audio is just what happens. Omit it and the server fetches. */
  fetchAudio?: boolean;
  /** Every card mined from a sentence inherits that sentence's two image slots. */
  image?: string;
  extraImage?: string;
  /** Which row was clicked. It leads the merged card; see `Definition.seq`. */
  entrySeq?: number | null;
  /** Merge archaic entries too. A clicked archaic row is included whatever this says. */
  includeArchaic?: boolean;
}

export interface CardResult {
  succeeded: boolean;
  duplicate: boolean;
  noteId: number | null;
  message: string | null;
  /**
   * The definition joined a card that already existed rather than creating one. Neither a plain
   * add nor a duplicate: a row was pressed and a card that was already there changed.
   */
  appended?: boolean;
  /**
   * Whether a recording actually landed. The card is added either way and a failed fetch tags
   * nothing (so the pipeline retries it), so this is the only signal that audio did not arrive —
   * which is how audio being broken for the whole Python cutover went unnoticed.
   */
  audioFetched: boolean;
}

export interface RadicalInfo {
  radical: string;
  strokeCount: number;
  /** Recognizable glyph to display instead of `radical` (RADKFILE labels some buckets with a stand-in kanji). */
  display: string | null;
  /** "left" / "right" — which side the glyph sits on in a kanji (for the 阝 pair), else null. */
  align: string | null;
}

export interface RadicalCatalogResponse {
  radicals: RadicalInfo[];
}

export interface KanjiResult {
  literal: string;
  strokeCount: number;
  frequency: number | null;
  meaning: string | null;
}

export interface KanjiByRadicalsResponse {
  kanji: KanjiResult[];
  availableRadicals: string[];
}

/** The picker's focused-kanji card — what confirms you found the right character before inserting. */
export interface KanjiDetail {
  literal: string;
  strokeCount: number | null;
  /** KANJIDIC2's grade: 1-6 kyouiku, 8 the rest of jouyou, 9-10 jinmeiyou, null outside all. */
  grade: number | null;
  frequency: number | null;
  onReadings: string[];
  kunReadings: string[];
  meanings: string[];
  examples: string[];
  /** How many of your own notes contain it. `null` — not 0 — when the collection could not be read. */
  inCollection: number | null;
}

/** Thrown for 503 so the UI can retry while "loading" and show the reason when "unavailable". */

// --- grammar (日本-498) -------------------------------------------------------------------------

/** How a DoJG entry met the query. **The results order reads this** — see `resultKind.ts`. */
export type GrammarMatch = "exact" | "contains" | "within";

export interface GrammarExample {
  /** The book's own: `ksa`/`ksb` are Key Sentences, `a`/`b`/`c` ordinary examples. */
  label: string;
  ja: string;
  en: string;
  /**
   * The construction AS INFLECTED in this sentence — `あげる (1)` gives `あげた`, `あげました`.
   * What marks the grammar point inside the example. Never a search key.
   */
  pattern: string[];
}

export interface GrammarFigure {
  /** Fetch from `/api/grammar/figure/{file}` — the bytes live in the dataset. */
  file: string;
  kind: string | null;
  caption: string | null;
}

export interface GrammarTable {
  title: string | null;
  figure: string | null;
  figureContinued: string | null;
  columns: string[];
  rows: string[][];
  note: string | null;
}

/** How well a row answered the query. Lower ranks sort first; shared across kinds. */
export type ResultRank = "exact" | "contains" | "within" | "fanout";

/**
 * One row of the merged pool **as it arrives on the wire**: exactly one of `vocab`/`grammar` is set.
 *
 * A tagged union rather than two parallel lists, because the order ACROSS kinds is the product —
 * two lists would hand the client back the merging problem the route exists to solve. Two *named*
 * keys rather than one polymorphic `entry`, because on the wire a key whose type depends on a
 * sibling field is a shape nothing can validate by reading it.
 */
export type WireResultRow =
  | { kind: "vocab"; rank: ResultRank; vocab: Definition }
  | { kind: "grammar"; rank: ResultRank; grammar: GrammarEntry };

/**
 * The same row **as the app draws it**, with the payload under one name.
 *
 * The two shapes differ on purpose and `searchAll` is the one place they meet. Every row component
 * reads `row.entry`, which is what it was before the ordering moved to the server (日本-513) — so
 * the wire can be explicit without that costing a rename across every component that draws a row.
 */
export type SearchAllRow =
  | { kind: "vocab"; rank: ResultRank; entry: Definition }
  | { kind: "grammar"; rank: ResultRank; entry: GrammarEntry };

export interface SearchAllResponse {
  rows: SearchAllRow[];
  /** Per kind, including a zero for a kind that produced nothing. */
  counts: Record<string, number>;
}

/** One DoJG entry. The same shape from search and from the by-id route, deliberately. */
export interface GrammarEntry {
  /** `basic-079`. Stable, and what a grammar card's `Entries` records. */
  id: string;
  /** The DISPLAY form, markup and all: `だけで(は)なく～(も)`. Never a search key. */
  concept: string;
  volume: string;
  page: string;
  partOfSpeech: string | null;
  meaning: string | null;
  usage: string | null;
  formation: string | null;
  /** Raw `【Related Expression: か2; かい】` — parsed into chips by the client. */
  related: string | null;
  antonym: string | null;
  notesText: string | null;
  notesSource: string | null;
  examples: GrammarExample[];
  figures: GrammarFigure[];
  tables: GrammarTable[];
  match: GrammarMatch;
  /** Which written form matched. `にかかわらず・に関/拘/係わらず` can be met by any of four. */
  matched: string;
}

export interface SearchGrammarResponse {
  entries: GrammarEntry[];
}

/**
 * One table-of-contents row (日本-519) — **not a thinned `GrammarEntry`**.
 *
 * The contents is 629 rows and an entry carries every example, figure and table; one shape for both
 * would put megabytes on the wire to draw a list. Two reads, two types, and the narrow one is what
 * a row renders from.
 *
 * `partOfSpeech` is the SOURCE string, in English. `surfaces/grammar/grammarPos.ts` is what turns
 * it into Japanese, and it fails open — see there.
 */
export interface GrammarSummary {
  id: string;
  concept: string;
  volume: string;
  page: string;
  partOfSpeech: string | null;
  meaning: string | null;
}

export interface GrammarIndexResponse {
  entries: GrammarSummary[];
}



export class SearchUnavailableError extends Error {
  state: string;
  constructor(message: string, state: string) {
    super(message);
    this.name = "SearchUnavailableError";
    this.state = state;
  }
}

async function errorMessage(res: Response): Promise<string> {
  bounceIfUnauthorized(res);
  try {
    const body = await res.json();
    // FastAPI's HTTPExceptions arrive as {detail}; only the app-level 502/401 handlers use {error}.
    if (body && typeof body.detail === "string") return body.detail;
    if (body && typeof body.error === "string") return body.error;
  } catch {
    // non-JSON body
  }
  return `Request failed (${res.status})`;
}

/** Wraps the shared 503 handling: the dictionary is still loading, or the pipeline is unavailable. */
async function throwIfUnavailable(res: Response): Promise<void> {
  if (res.status !== 503) return;
  let state = "loading";
  let reason = "Loading dictionary…";
  try {
    const body = await res.json();
    if (body?.state) state = body.state;
    if (body?.reason) reason = body.reason;
  } catch {
    // non-JSON body
  }
  throw new SearchUnavailableError(reason, state);
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // `addCard` is THE write this tab already accounts for — `Read` holds an optimistic ±1 for it
      // — so it has to be attributed here or the tab acts on its own frame. Without the header the
      // add is announced as nobody's, and the timing makes that actively wrong rather than merely
      // wasteful: the server publishes the frame the moment the note write lands but only responds
      // after the audio fetch (`fetch_audio` defaults to true), so the provoked `/api/decks/counts`
      // read usually resolves FIRST, storing the post-add truth — and then `onAdjustCounts(1)` adds
      // a second +1 on top of it. `N new` and `N added` moved by two per card added.
      //
      // Everything else here is a read, so carrying the id costs nothing and keeps one rule for the
      // whole module rather than one exception in it. See `clientId.ts`.
      [CLIENT_ID_HEADER]: CLIENT_ID,
    },
    body: JSON.stringify(body),
    cache: "no-store",
  });
  await throwIfUnavailable(res);
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json();
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { cache: "no-store" });
  await throwIfUnavailable(res);
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json();
}

/** Tokenize query text into the furigana strip. */
export function parseText(text: string): Promise<SearchParseResponse> {
  return postJson<SearchParseResponse>("/api/search/parse", { text });
}

/**
 * Parse many sentences in one request.
 *
 * **Opening a persisted reading log is what this is for** (日本-207). A log runs to several hundred
 * sentences and every one needs its tokens before the screen means anything — the underlines, the
 * three known-word states and the hover pivot all hang off them. One request per sentence is not a
 * slow read, it is an unusable one.
 *
 * The answer is **positional**: `results[i]` belongs to `texts[i]`, always, same length. Two
 * sentences in a log are very often identical, so a map keyed by text would quietly collapse them.
 */
export function parseTexts(texts: string[]): Promise<{ results: SearchParseResponse[] }> {
  return postJson<{ results: SearchParseResponse[] }>("/api/search/parse-batch", { texts });
}

/** Look up dictionary entries for the word at a character position. */
export function lookup(text: string, index: number): Promise<SearchLookupResponse> {
  return postJson<SearchLookupResponse>("/api/search/lookup", { text, index });
}


/**
 * Which DoJG grammar entries this query names (日本-498).
 *
 * **A separate route from `/lookup` and it stays one**: `/lookup` is pinned by the golden fixtures
 * and answers a `Definition`, which a grammar entry is not, so merging into either would move a
 * fixture.
 *
 * **Nothing in the app calls this any more — `searchAll` does** (日本-513). It is kept because the
 * route it wraps is half of what that one composes, and because grammar alone stays a real
 * question — the MCP's `grammar_search` asks exactly it. What moved to the server is the
 * *ordering*, not either route. (`GrammarCard`'s related expressions are **not** a caller: those
 * chips are `relatedChips` over a raw field, and they are drawn as text rather than looked up.)
 *
 * **`index` and `anchorLength` are `lookup`'s pivot, meaning the same thing** (日本-508): the word
 * at `index` is the question and what follows it is the context a construction may run into.
 * Omitting `anchorLength` asks about the whole string, which is right for a typed query and wrong
 * for a hover — a sentence names every construction anywhere inside it.
 */
export function grammarSearch(
  text: string,
  index = 0,
  anchorLength?: number,
): Promise<SearchGrammarResponse> {
  return postJson<SearchGrammarResponse>("/api/grammar/search", { text, index, anchorLength });
}

/**
 * The merged result of both dictionaries, ordered — one request, one rule (日本-513).
 *
 * **This replaced two requests and a client-side merge.** The ordering used to live in
 * `resultKind.ts` and was the only place the two answers became an order; the MCP became a third
 * caller of that rule and a second implementation in a second language is the drift this codebase
 * keeps writing rules against. So `nihongonator/engine/ranking.py` owns it and this asks for the
 * answer.
 *
 * Every exact match first with both kinds interleaved, then the fan-out. Within a rank each kind
 * keeps the order its own route gave it. `counts` is per kind, and a kind that produced nothing is
 * still a key with a zero — whether to draw it is `countsLabel`'s decision.
 *
 * **`anchorLength` is what makes a hover different from a typed query**, exactly as on the two
 * routes below it: the word at `index` is the question, and the server measures a dictionary row's
 * exactness against *that* rather than against the sentence it sits in (日本-508).
 */
export async function searchAll(
  text: string,
  index = 0,
  anchorLength?: number,
): Promise<SearchAllResponse> {
  const answer = await postJson<{ rows: WireResultRow[]; counts: Record<string, number> }>(
    "/api/search/all",
    { text, index, anchorLength },
  );
  return {
    // The wire's two named keys become the one the components read. Here rather than in the hook,
    // so there is exactly one place the two shapes meet and no component ever sees the wire form.
    rows: answer.rows.map((row) =>
      row.kind === "vocab"
        ? ({ kind: "vocab", rank: row.rank, entry: row.vocab } as const)
        : ({ kind: "grammar", rank: row.rank, entry: row.grammar } as const),
    ),
    counts: answer.counts,
  };
}

/**
 * The table of contents — every entry, in the book's own order (日本-519).
 *
 * **Fetched once and filtered on the client.** 629 rows is ~80 KB, and every filter 文法 offers —
 * volume and 品詞 — is answerable from what is already in hand, so a server round trip per keystroke
 * would buy nothing. Searching is the other question and stays `grammarSearch`'s, because the key
 * rule lives in `engine/grammar_keys.py` and nothing else may restate it.
 */
export function grammarIndex(): Promise<GrammarIndexResponse> {
  return getJson<GrammarIndexResponse>("/api/grammar/index");
}

/** One entry in full, by id — what an expanded row and a deep link both read. */
export function grammarEntry(id: string): Promise<GrammarEntry> {
  return getJson<GrammarEntry>(`/api/grammar/${encodeURIComponent(id)}`);
}

/** The URL a figure's PNG is served from. Immutable for the life of the deploy. */
export function grammarFigureUrl(file: string): string {
  return `${API_BASE}/api/grammar/figure/${encodeURIComponent(file)}`;
}


/** Build a fully-enriched note for a chosen entry and add it to the vocab deck. */
export function addCard(req: AddCardRequest): Promise<CardResult> {
  return postJson<CardResult>("/api/search/card", req);
}

/**
 * What the app knows about each term: you have this card, you have the word elsewhere, or neither.
 *
 * `known` means a card is filed under this **exact** string and nothing looser — that is the rule,
 * and it is what the `+ add` / `✓ in deck` button reads. `elsewhere` is the weaker answer for a
 * word you hold under a different spelling; it names the card in `cardedAs` so the dock can say
 * which, and it draws a dashed underline rather than a solid one.
 *
 * Replaced `POST /card-status`, which answered `Record<string, boolean>`. The path changed rather
 * than the payload deliberately: a tab open across the deploy would otherwise post to a live path,
 * read every object value as truthy, and mark every word on screen as owned with no error to
 * catch. A removed path 404s, `postJson` throws, and the underlines simply fall back.
 */
export type CardState = "known" | "elsewhere" | "unknown";

export interface CardCarrier {
  /** The `Japanese` field of the card covering this word. */
  term: string;
  seq: number;
  /** How many MORE cards hold the same entry — rendered as `+1`. */
  others: number;
}

export interface CardSignalResponse {
  /** Every term that was asked about, including the `unknown` ones — see `mergeCardSignal`. */
  state: Record<string, CardState>;
  /** Sparse: `elsewhere` terms only. */
  cardedAs: Record<string, CardCarrier>;
}

export function cardSignal(terms: string[]): Promise<CardSignalResponse> {
  return postJson<CardSignalResponse>("/api/search/card-signal", { terms });
}

/**
 * Which dictionary entries each term's existing card already holds, by JMdict `seq`.
 *
 * Every entry written the same way shares one card, so the dock shows a row per entry and needs to
 * say which are already there. An empty list means "nothing recorded" — no card, or a card from
 * before the `Entries` field existed — and every row offers to add. The server refuses a genuine
 * duplicate regardless, so a wrong guess here costs a message, not a bad write.
 */
export function cardEntries(terms: string[]): Promise<Record<string, number[]>> {
  return postJson<Record<string, number[]>>("/api/search/card-entries", { terms });
}

/** The full radical picker palette (every radical + its stroke count). */
export function getRadicals(): Promise<RadicalCatalogResponse> {
  return getJson<RadicalCatalogResponse>("/api/search/radicals");
}

/** Kanji containing all selected radicals, plus the radicals still worth offering. */
export function kanjiByRadicals(radicals: string[]): Promise<KanjiByRadicalsResponse> {
  return postJson<KanjiByRadicalsResponse>("/api/search/kanji-by-radicals", { radicals });
}

/**
 * Readings, meanings, examples and your own coverage of one character.
 *
 * Resolves to null for a character KANJIDIC2 has no entry for (the server 404s), which is a normal
 * answer here rather than a failure — the caller just shows no card.
 */
export async function kanjiDetail(literal: string): Promise<KanjiDetail | null> {
  const res = await fetch(`${API_BASE}/api/search/kanji/${encodeURIComponent(literal)}`, {
    cache: "no-store",
  });
  if (res.status === 404) return null;
  await throwIfUnavailable(res);
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json();
}
